import { useRef, useState } from "react";
import { Link, Navigate, useNavigate, useParams, useSearchParams } from "react-router-dom";
import { ArrowLeft, CalendarDays, Ship, Hotel, Copy, Package, Sparkles, GripVertical, Plus, Trash2, Link2, Users, Pencil, Eye, EyeOff, Zap, Clock, ShieldCheck, Wine, Car, Compass, UtensilsCrossed, MoreHorizontal, Upload, Image as ImageIcon, Loader2, Globe, DollarSign, Mail, Phone, FileText, CheckCircle2 } from "lucide-react";
import { toast } from "sonner";
import { useDemo } from "@/store/DemoStore";
import { api, errMsg } from "@/lib/api";
import { Card, CardTitle, Empty, btn } from "@/components/common";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Switch } from "@/components/ui/switch";
import { cn } from "@/lib/utils";
import { fmtDate, fmtMoney, pkgFromPrice, fmtShort, addDays } from "@/lib/trip";

const ADDON_TYPES = [
  ["Travel Insurance", ShieldCheck],
  ["Drink Package", Wine],
  ["Transfer", Car],
  ["Excursion", Compass],
  ["Specialty Dining", UtensilsCrossed],
  ["Other", MoreHorizontal],
];
const addonIcon = (t) => (ADDON_TYPES.find(([n]) => n === t) || [null, Sparkles])[1];

const field = "h-11 w-full rounded-xl border border-slate-200 bg-white px-3.5 text-base text-navy-900 outline-none focus:border-navy-500 focus:ring-2 focus:ring-navy-100 sm:text-sm";
const label = "mb-1.5 block text-xs font-medium text-slate-500";
const TABS = [["overview", "Overview"], ["packages", "Packages"], ["addons", "Add-Ons"], ["itinerary", "Itinerary"], ["bookings", "Bookings"]];

function Hero({ group }) {
  const TypeIcon = group.type === "Resort" ? Hotel : Ship;
  const dates = [group.departure, group.returnDate].filter(Boolean).map(fmtDate).join(" – ");
  return (
    <div className="relative -mx-4 -mt-6 overflow-hidden sm:mx-0 sm:mt-0 sm:rounded-3xl">
      {group.cover
        ? <img src={group.cover} alt="" referrerPolicy="no-referrer" className="absolute inset-0 h-full w-full object-cover" onLoad={(e) => { e.currentTarget.style.display = ""; }} onError={(e) => { e.currentTarget.style.display = "none"; }} />
        : <div className="absolute inset-0 bg-gradient-to-br from-navy-800 to-navy-950" />}
      <div className="absolute inset-0 bg-gradient-to-t from-navy-950/90 via-navy-950/50 to-navy-950/20" />
      <div className="relative flex min-h-[220px] flex-col justify-between gap-6 p-5 sm:min-h-[240px] sm:p-8">
        <div className="flex items-center justify-between gap-3">
          <Link to="/agent/groups" data-testid="back-to-groups" className="inline-flex items-center gap-1.5 rounded-full bg-white/10 px-3 py-1.5 text-xs font-medium text-white backdrop-blur hover:bg-white/20"><ArrowLeft className="h-3.5 w-3.5" />All Groups</Link>
          {group.published
            ? <span className="rounded-full bg-emerald-500/90 px-3 py-1 text-[11px] font-semibold text-white backdrop-blur" data-testid="group-published-badge">Published</span>
            : <span className="rounded-full bg-white/15 px-3 py-1 text-[11px] font-semibold text-white backdrop-blur" data-testid="group-draft-badge">Draft</span>}
        </div>
        <div className="min-w-0 text-white">
          <span className="inline-flex items-center gap-1.5 text-sm font-medium text-white/70"><TypeIcon className="h-3.5 w-3.5" />{group.type}</span>
          <h1 className="mt-1 font-display text-2xl font-bold tracking-tight sm:text-4xl" data-testid="group-title">{group.name}</h1>
          <p className="mt-2 flex flex-wrap items-center gap-x-4 gap-y-1 text-sm text-white/75">
            {group.destination && <span>{group.destination}</span>}
            {dates && <span className="inline-flex items-center gap-1.5"><CalendarDays className="h-3.5 w-3.5" />{dates}</span>}
          </p>
        </div>
      </div>
    </div>
  );
}

function Counter({ icon: Icon, label: l, value, testId }) {
  return (
    <Card className="flex items-center gap-4 p-5" data-testid={testId}>
      <span className="grid h-11 w-11 shrink-0 place-items-center rounded-xl bg-navy-50 text-navy-700"><Icon className="h-5 w-5" /></span>
      <div>
        <p className="font-display text-2xl font-bold leading-none text-navy-900">{value}</p>
        <p className="mt-1 text-xs font-medium uppercase tracking-wider text-slate-400">{l}</p>
      </div>
    </Card>
  );
}

function PublicUrlCard({ group, canEdit }) {
  const { updateGroup } = useDemo();
  const url = `${window.location.origin}/g/${group.code}`;
  const copy = () => { navigator.clipboard?.writeText(url); toast.success("Link copied"); };
  const togglePublish = (v) => updateGroup(group.id, { published: v });
  return (
    <Card data-testid="group-public-url-card">
      <CardTitle>Public Page</CardTitle>
      <div className="flex items-center justify-between gap-4 border-b border-slate-100 p-5 sm:p-6">
        <div className="min-w-0">
          <p className="flex items-center gap-2 text-sm font-semibold text-navy-900"><Globe className="h-4 w-4 text-navy-500" />{group.published ? "Published" : "Draft"}</p>
          <p className="mt-1 text-xs text-slate-400">{group.published ? "Travelers can view this page and request to book." : "Turn on to make this page public for travelers."}</p>
        </div>
        <Switch checked={!!group.published} onCheckedChange={togglePublish} disabled={!canEdit} data-testid="group-publish-toggle" />
      </div>
      <div className="flex flex-col gap-3 p-5 sm:flex-row sm:items-center sm:p-6">
        <div className="flex min-w-0 flex-1 items-center gap-2 rounded-xl border border-slate-200 bg-slate-50 px-3.5 py-2.5">
          <Link2 className="h-4 w-4 shrink-0 text-slate-400" />
          <code className="truncate font-mono text-sm text-navy-800" data-testid="group-public-url">{url}</code>
        </div>
        <div className="flex gap-2">
          <button onClick={copy} className={btn.secondary} data-testid="group-copy-url-btn"><Copy className="h-4 w-4" />Copy</button>
          {group.published && <a href={url} target="_blank" rel="noreferrer" className={btn.secondary} data-testid="group-open-url-btn"><Globe className="h-4 w-4" />Open</a>}
        </div>
      </div>
    </Card>
  );
}

function OverviewForm({ group, canEdit }) {
  const { updateGroup } = useDemo();
  const [f, setF] = useState({
    name: group.name, type: group.type, destination: group.destination || "", supplier: group.supplier || "",
    vessel: group.vessel || "", departure: group.departure || "", returnDate: group.returnDate || "", cover: group.cover || "", description: group.description || "",
    depositMode: group.depositMode || "per_person", depositAmount: group.depositAmount ?? 0, finalPaymentDue: group.finalPaymentDue || "",
  });
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const fileRef = useRef(null);
  const ro = !canEdit;
  const set = (k) => (e) => setF((p) => ({ ...p, [k]: e.target.value }));
  const pickFile = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const fd = new FormData();
    fd.append("file", file);
    setUploading(true);
    try {
      const { data } = await api.post(`/groups/${group.id}/cover-image`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      setF((p) => ({ ...p, cover: data.url }));
      toast.success("Cover photo uploaded — save changes to apply");
    } catch (err) { toast.error(errMsg(err)); }
    setUploading(false);
    if (fileRef.current) fileRef.current.value = "";
  };
  const save = async () => {
    setSaving(true);
    await updateGroup(group.id, { ...f, depositAmount: Number(f.depositAmount) || 0 });
    setSaving(false);
  };

  return (
    <div className="grid gap-5 lg:grid-cols-2">
      {ro && <div className="rounded-xl border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-800 lg:col-span-2" data-testid="group-readonly-notice">View only — only {group.agentName || "the group's advisor"} or an admin can edit this group's details.</div>}
      <Card data-testid="group-info-card">
        <CardTitle>Group Information</CardTitle>
        <div className="grid gap-3 p-5 sm:grid-cols-2 sm:p-6">
          <label className="sm:col-span-2"><span className={label}>Group name</span><input className={field} value={f.name} onChange={set("name")} disabled={ro} data-testid="group-edit-name" /></label>
          <label><span className={label}>Type</span>
            <select className={field} value={f.type} onChange={set("type")} disabled={ro} data-testid="group-edit-type"><option value="Cruise">Cruise</option><option value="Resort">Resort</option></select></label>
          <label><span className={label}>Destination</span><input className={field} value={f.destination} onChange={set("destination")} disabled={ro} data-testid="group-edit-destination" /></label>
          <label><span className={label}>Supplier</span><input className={field} value={f.supplier} onChange={set("supplier")} disabled={ro} data-testid="group-edit-supplier" /></label>
          <label><span className={label}>{f.type === "Resort" ? "Resort" : "Ship"}</span><input className={field} value={f.vessel} onChange={set("vessel")} disabled={ro} data-testid="group-edit-vessel" /></label>
          <label><span className={label}>Departure date</span><input type="date" className={field} value={f.departure} onChange={set("departure")} disabled={ro} data-testid="group-edit-departure" /></label>
          <label><span className={label}>Return date</span><input type="date" className={field} value={f.returnDate} onChange={set("returnDate")} disabled={ro} data-testid="group-edit-return" /></label>
          <div className="sm:col-span-2">
            <span className={label}>Cover photo</span>
            <div className="flex items-center gap-4">
              <div className="grid h-24 w-40 shrink-0 place-items-center overflow-hidden rounded-xl border border-slate-200 bg-slate-50">
                {f.cover ? <img src={f.cover} alt="" referrerPolicy="no-referrer" className="h-full w-full object-cover" /> : <ImageIcon className="h-7 w-7 text-slate-300" />}
              </div>
              <div>
                <input ref={fileRef} type="file" accept="image/jpeg,image/png,image/webp,image/gif" className="hidden" onChange={pickFile} data-testid="group-cover-input" />
                <button type="button" onClick={() => fileRef.current?.click()} disabled={ro || uploading} className={btn.secondary} data-testid="group-cover-upload-btn">
                  {uploading ? <><Loader2 className="h-4 w-4 animate-spin" />Uploading…</> : <><Upload className="h-4 w-4" />{f.cover ? "Replace photo" : "Upload photo"}</>}
                </button>
                <p className="mt-1.5 text-xs text-slate-400">JPG, PNG, WEBP or GIF · up to 8MB</p>
              </div>
            </div>
          </div>
          <label className="sm:col-span-2"><span className={label}>Trip description</span>
            <textarea className={cn(field, "h-28 resize-none py-2.5 leading-relaxed")} value={f.description} onChange={set("description")} disabled={ro} placeholder="Describe this group experience for travelers — what makes it special, what's included…" data-testid="group-edit-description" /></label>
        </div>
      </Card>

      <Card data-testid="group-deposit-card">
        <CardTitle>Deposit &amp; payment details</CardTitle>
        <div className="grid gap-3 p-5 sm:p-6">
          <div>
            <span className={label}>Deposit mode</span>
            <div className="flex gap-2">
              {[["per_person", "Per person"], ["per_booking", "Per booking"]].map(([v, l]) => (
                <button key={v} type="button" disabled={ro} onClick={() => setF((p) => ({ ...p, depositMode: v }))} data-testid={`group-deposit-mode-${v}`}
                  className={cn("flex-1 rounded-xl border px-3 py-2.5 text-sm font-medium transition-colors disabled:opacity-60", f.depositMode === v ? "border-navy-900 bg-navy-900 text-white" : "border-slate-200 bg-white text-slate-600 hover:border-navy-300")}>{l}</button>
              ))}
            </div>
          </div>
          <label><span className={label}>Deposit amount (USD)</span>
            <input type="number" min="0" step="0.01" className={field} value={f.depositAmount} onChange={set("depositAmount")} disabled={ro} data-testid="group-edit-deposit-amount" /></label>
          <label><span className={label}>Final payment due</span>
            <input type="date" className={field} value={f.finalPaymentDue} onChange={set("finalPaymentDue")} disabled={ro} data-testid="group-edit-final-due" /></label>
        </div>
      </Card>

      {canEdit && (
        <div className="flex justify-end lg:col-span-2">
          <button onClick={save} disabled={saving} className={btn.primary} data-testid="group-save-overview-btn">{saving ? "Saving…" : "Save Changes"}</button>
        </div>
      )}
    </div>
  );
}

function Overview({ group, canEdit }) {
  const { groupBookingsFor } = useDemo();
  const bookings = groupBookingsFor(group.id);
  const packages = group.packages || [];
  const totalSpots = packages.reduce((s, p) => s + (Number(p.capacity) || 0), 0);
  const seats = bookings.reduce((s, b) => s + (Number(b.travelers) || 1), 0);
  const spots = packages.length ? Math.max(0, totalSpots - seats) : "—";
  return (
    <div className="space-y-5">
      <div className="grid gap-4 sm:grid-cols-3">
        <Counter icon={Package} label="Total Packages" value={packages.length} testId="group-counter-packages" />
        <Counter icon={Users} label="Total Bookings" value={bookings.length} testId="group-counter-bookings" />
        <Counter icon={Ship} label="Spots Remaining" value={spots} testId="group-counter-spots" />
      </div>
      <PublicUrlCard group={group} canEdit={canEdit} />
      <OverviewForm group={group} canEdit={canEdit} />
    </div>
  );
}

const emptyBooking = () => ({ name: "", email: "", phone: "", travelers: 1, packageId: "", note: "" });

function BookingModal({ open, onClose, group, onSave }) {
  const [f, setF] = useState(emptyBooking());
  const [saving, setSaving] = useState(false);
  const set = (k) => (e) => setF((p) => ({ ...p, [k]: e.target.value }));
  const save = async () => {
    if (!f.name.trim()) { toast.error("Enter a name for this booking"); return; }
    setSaving(true);
    const ok = await onSave({ ...f, name: f.name.trim(), email: f.email.trim(), phone: f.phone.trim(), travelers: Number(f.travelers) || 1, note: f.note.trim() });
    setSaving(false);
    if (ok) onClose();
  };
  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-lg" data-testid="group-booking-modal">
        <DialogHeader><DialogTitle>Add a Booking</DialogTitle></DialogHeader>
        <div className="grid gap-3 sm:grid-cols-2">
          <label className="sm:col-span-2"><span className={label}>Name</span><input className={field} value={f.name} onChange={set("name")} data-testid="booking-name" /></label>
          <label><span className={label}>Email</span><input className={field} value={f.email} onChange={set("email")} placeholder="Optional" data-testid="booking-email" /></label>
          <label><span className={label}>Phone</span><input className={field} value={f.phone} onChange={set("phone")} placeholder="Optional" data-testid="booking-phone" /></label>
          <label><span className={label}>Travelers</span><input type="number" min="1" className={field} value={f.travelers} onChange={set("travelers")} data-testid="booking-travelers" /></label>
          <label><span className={label}>Package</span>
            <select className={field} value={f.packageId} onChange={set("packageId")} data-testid="booking-package">
              <option value="">No package</option>
              {(group.packages || []).map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}
            </select></label>
          <label className="sm:col-span-2"><span className={label}>Note</span><textarea className={cn(field, "h-20 resize-none py-2.5 leading-relaxed")} value={f.note} onChange={set("note")} placeholder="Internal note (optional)" data-testid="booking-note" /></label>
        </div>
        <div className="mt-2 flex justify-end gap-2">
          <button onClick={onClose} className={btn.secondary} data-testid="booking-cancel">Cancel</button>
          <button onClick={save} disabled={saving} className={btn.primary} data-testid="booking-save">{saving ? "Adding…" : "Add booking"}</button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

function Bookings({ group }) {
  const { groupBookingsFor, addGroupBooking, updateGroupBooking, deleteGroupBooking, convertBookingToQuote, agentName, me, isAdmin } = useDemo();
  const navigate = useNavigate();
  const [adding, setAdding] = useState(false);
  const [busy, setBusy] = useState(null);
  const bookings = groupBookingsFor(group.id);
  const canManage = (b) => isAdmin || b.agentId === me?.id;
  const seats = bookings.reduce((s, b) => s + (Number(b.travelers) || 1), 0);
  const totalSpots = (group.packages || []).reduce((s, p) => s + (Number(p.capacity) || 0), 0);
  const spots = (group.packages || []).length ? Math.max(0, totalSpots - seats) : "—";

  const onAdd = async (body) => !!(await addGroupBooking(group.id, body));
  const toggle = async (b) => { setBusy(b.id); await updateGroupBooking(group.id, b.id, b.status === "booked" ? "pending" : "booked"); setBusy(null); };
  const remove = async (b) => {
    if (!window.confirm(`Remove booking for ${b.name}? This frees up their spot${(b.travelers || 1) > 1 ? "s" : ""}.`)) return;
    setBusy(b.id); await deleteGroupBooking(group.id, b.id); setBusy(null);
  };
  const convert = async (b) => {
    setBusy(b.id);
    const r = await convertBookingToQuote(group.id, b.id);
    setBusy(null);
    if (r?.tripId) { toast.success(`Quote ${r.quoteNumber} created`); navigate(`/agent/quotes/${r.tripId}`); }
  };

  return (
    <div className="space-y-5" data-testid="group-bookings">
      <div className="grid gap-4 sm:grid-cols-3">
        <Counter icon={Users} label="Total Bookings" value={bookings.length} testId="bookings-counter-total" />
        <Counter icon={Users} label="Seats Held" value={seats} testId="bookings-counter-seats" />
        <Counter icon={Ship} label="Spots Remaining" value={spots} testId="bookings-counter-spots" />
      </div>
      <div className="flex items-center justify-between gap-3">
        <div>
          <h2 className="font-display text-lg font-bold text-navy-900">Bookings</h2>
          <p className="text-sm text-slate-500">Pending and booked travelers both hold a spot.</p>
        </div>
        <button onClick={() => setAdding(true)} className={btn.secondary} data-testid="group-add-booking-btn"><Plus className="h-4 w-4" />Add Booking</button>
      </div>
      {bookings.length ? (
        <div className="space-y-3">
          {bookings.map((b) => (
            <Card key={b.id} className="p-4 sm:p-5" data-testid={`booking-row-${b.id}`}>
              <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
                <div className="min-w-0">
                  <div className="flex flex-wrap items-center gap-2">
                    <p className="font-semibold text-navy-900">{b.name}</p>
                    <span className={cn("rounded-full px-2 py-0.5 text-[11px] font-semibold", b.status === "booked" ? "bg-emerald-100 text-emerald-700" : "bg-amber-100 text-amber-700")} data-testid="booking-status-badge">{b.status === "booked" ? "Booked" : "Pending"}</span>
                    {b.quoteId && <span className="rounded-full bg-navy-100 px-2 py-0.5 text-[11px] font-semibold text-navy-700">Quoted</span>}
                    {b.source === "client" && <span className="rounded-full bg-slate-100 px-2 py-0.5 text-[11px] font-semibold text-slate-600">Web request</span>}
                  </div>
                  <p className="mt-1.5 flex flex-wrap items-center gap-x-4 gap-y-1 text-sm text-slate-500">
                    {b.email && <span className="inline-flex items-center gap-1.5"><Mail className="h-3.5 w-3.5" />{b.email}</span>}
                    {b.phone && <span className="inline-flex items-center gap-1.5"><Phone className="h-3.5 w-3.5" />{b.phone}</span>}
                    <span className="inline-flex items-center gap-1.5"><Users className="h-3.5 w-3.5" />{b.travelers} traveler{b.travelers > 1 ? "s" : ""}</span>
                    {b.packageName && <span className="inline-flex items-center gap-1.5"><Package className="h-3.5 w-3.5" />{b.packageName}</span>}
                  </p>
                  {b.note && <p className="mt-2 text-sm text-slate-600">{b.note}</p>}
                </div>
                <div className="flex shrink-0 flex-wrap gap-2">
                  {canManage(b) ? (
                    <>
                      <button onClick={() => toggle(b)} disabled={busy === b.id} className={btn.secondary} data-testid="booking-toggle-status">{b.status === "booked" ? <><Clock className="h-4 w-4" />Mark pending</> : <><CheckCircle2 className="h-4 w-4" />Confirm</>}</button>
                      {b.quoteId
                        ? <Link to={`/agent/quotes/${b.quoteId}`} className={btn.secondary} data-testid="booking-view-quote"><FileText className="h-4 w-4" />View Quote</Link>
                        : <button onClick={() => convert(b)} disabled={busy === b.id} className={btn.secondary} data-testid="booking-convert-quote"><FileText className="h-4 w-4" />Convert to Quote</button>}
                      <button onClick={() => remove(b)} disabled={busy === b.id} className="grid h-10 w-10 shrink-0 place-items-center rounded-lg text-slate-400 transition-colors hover:bg-red-50 hover:text-red-600" data-testid="booking-delete"><Trash2 className="h-4 w-4" /></button>
                    </>
                  ) : (
                    <span className="inline-flex items-center gap-1.5 self-center rounded-full bg-slate-100 px-3 py-1 text-xs font-medium text-slate-500" data-testid="booking-locked"><ShieldCheck className="h-3.5 w-3.5" />{agentName(b.agentId) || "Another advisor"}</span>
                  )}
                </div>
              </div>
            </Card>
          ))}
        </div>
      ) : <Empty icon={Users} title="No bookings yet" text="Add a booking manually, or share the public page so travelers can request to join." />}
      {adding && <BookingModal open onClose={() => setAdding(false)} group={group} onSave={onAdd} />}
    </div>
  );
}

function Itinerary({ group, canEdit }) {
  const { updateGroup } = useDemo();
  const [days, setDays] = useState(group.days || []);
  const [saving, setSaving] = useState(false);
  const dirty = JSON.stringify(days) !== JSON.stringify(group.days || []);
  const add = () => setDays((d) => [...d, { id: `d_${Date.now()}`, title: "" }]);
  const edit = (id, title) => setDays((d) => d.map((x) => (x.id === id ? { ...x, title } : x)));
  const remove = (id) => setDays((d) => d.filter((x) => x.id !== id));
  const save = async () => { setSaving(true); await updateGroup(group.id, { days }); setSaving(false); };
  const dayDate = (i) => (group.departure ? fmtShort(addDays(group.departure, i)) : null);

  return (
    <div className="mx-auto max-w-2xl space-y-4" data-testid="group-itinerary">
      <div className="flex items-center justify-between">
        <h2 className="font-display text-lg font-bold text-navy-900">Itinerary</h2>
        {canEdit && <button onClick={add} className={btn.secondary} data-testid="group-add-day-btn"><Plus className="h-4 w-4" />Add Day</button>}
      </div>
      {days.length ? (
        <div className="space-y-3">
          {days.map((d, i) => (
            <Card key={d.id} className="flex items-center gap-3 p-3.5" data-testid={`group-day-${i}`}>
              <GripVertical className="h-4 w-4 shrink-0 text-slate-300" />
              <span className="grid h-12 w-16 shrink-0 place-items-center gap-0.5 rounded-lg bg-navy-50 px-1 text-navy-700">
                <span className="text-xs font-semibold uppercase tracking-wider">Day {i + 1}</span>
                {dayDate(i) && <span className="text-[10px] font-medium text-navy-500" data-testid={`group-day-date-${i}`}>{dayDate(i)}</span>}
              </span>
              <input className={cn(field, "flex-1")} placeholder="Day title (e.g. At sea, Nassau, Arrival)" value={d.title} onChange={(e) => edit(d.id, e.target.value)} disabled={!canEdit} data-testid={`group-day-title-${i}`} />
              {canEdit && <button onClick={() => remove(d.id)} className="grid h-10 w-10 shrink-0 place-items-center rounded-lg text-slate-400 transition-colors hover:bg-red-50 hover:text-red-600" data-testid={`group-day-remove-${i}`}><Trash2 className="h-4 w-4" /></button>}
            </Card>
          ))}
        </div>
      ) : <Empty icon={CalendarDays} title="No days yet" text="Add a day to start building the group itinerary." />}
      {canEdit && dirty && (
        <div className="flex justify-end">
          <button onClick={save} disabled={saving} className={btn.primary} data-testid="group-save-itinerary-btn">{saving ? "Saving…" : "Save itinerary"}</button>
        </div>
      )}
    </div>
  );
}

const emptyPackage = () => ({ id: `pk_${Date.now()}`, name: "", cover: "", capacity: "", description: "", pricing: [{ travelers: 1, price: "" }, { travelers: 2, price: "" }] });

const initTiers = (p) => (Array.isArray(p?.pricing) && p.pricing.length)
  ? p.pricing.map((t) => ({ travelers: Number(t.travelers) || 1, price: t.price ?? "" }))
  : [{ travelers: 1, price: p?.price ?? "" }, { travelers: 2, price: "" }];

function PackageModal({ open, onClose, initial, onSave, groupId }) {
  const [f, setF] = useState(() => ({ ...initial, pricing: initTiers(initial) }));
  const [uploading, setUploading] = useState(false);
  const fileRef = useRef(null);
  const set = (k) => (e) => setF((p) => ({ ...p, [k]: e.target.value }));
  const pickFile = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const fd = new FormData();
    fd.append("file", file);
    setUploading(true);
    try {
      const { data } = await api.post(`/groups/${groupId}/package-image`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      setF((p) => ({ ...p, cover: data.url }));
      toast.success("Image uploaded");
    } catch (err) { toast.error(errMsg(err)); }
    setUploading(false);
    if (fileRef.current) fileRef.current.value = "";
  };
  const setTier = (idx, val) => setF((p) => ({ ...p, pricing: p.pricing.map((t, i) => (i === idx ? { ...t, price: val } : t)) }));
  const addTier = () => setF((p) => ({ ...p, pricing: [...p.pricing, { travelers: (p.pricing.reduce((m, t) => Math.max(m, t.travelers), 0) || 0) + 1, price: "" }] }));
  const removeTier = (idx) => setF((p) => ({ ...p, pricing: p.pricing.filter((_, i) => i !== idx) }));
  const save = () => {
    const { _edit, price, ...rest } = f;
    if (!rest.name.trim()) return toast.error("Name this package");
    const tiers = (rest.pricing || []).filter((t) => t.price !== "" && Number(t.price) >= 0).map((t) => ({ travelers: Number(t.travelers) || 1, price: Number(t.price) || 0 }));
    if (!tiers.length) return toast.error("Enter at least one per-person price");
    onSave({ ...rest, name: rest.name.trim(), description: rest.description.trim(), capacity: Number(rest.capacity) || 0, pricing: tiers });
  };
  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-lg" data-testid="package-modal">
        <DialogHeader><DialogTitle>{initial._edit ? "Edit package" : "Add package"}</DialogTitle></DialogHeader>
        <div className="space-y-4">
          <div>
            <span className={label}>Cover photo</span>
            <div className="flex items-center gap-4">
              <div className="grid h-24 w-32 shrink-0 place-items-center overflow-hidden rounded-xl border border-slate-200 bg-slate-50">
                {f.cover ? <img src={f.cover} alt="" className="h-full w-full object-cover" /> : <ImageIcon className="h-7 w-7 text-slate-300" />}
              </div>
              <div>
                <input ref={fileRef} type="file" accept="image/jpeg,image/png,image/webp,image/gif" className="hidden" onChange={pickFile} data-testid="package-image-input" />
                <button type="button" onClick={() => fileRef.current?.click()} disabled={uploading} className={btn.secondary} data-testid="package-upload-btn">
                  {uploading ? <><Loader2 className="h-4 w-4 animate-spin" />Uploading…</> : <><Upload className="h-4 w-4" />{f.cover ? "Replace" : "Upload"}</>}
                </button>
                <p className="mt-1.5 text-xs text-slate-400">JPG, PNG, WEBP or GIF · up to 8MB</p>
              </div>
            </div>
          </div>
          <label className="block"><span className={label}>Package name</span><input className={field} value={f.name} onChange={set("name")} placeholder="e.g. Balcony Stateroom" data-testid="package-name" /></label>
          <label className="block"><span className={label}>Capacity (total spots)</span><input type="number" min="0" className={field} value={f.capacity} onChange={set("capacity")} placeholder="0" data-testid="package-capacity" /></label>
          <div>
            <span className={label}>Price per person (USD) — by number of travelers</span>
            <div className="space-y-2">
              {f.pricing.map((t, idx) => (
                <div key={idx} className="flex items-center gap-2" data-testid={`package-tier-${t.travelers}`}>
                  <span className="w-28 shrink-0 text-sm font-medium text-navy-800">{t.travelers === 1 ? "Solo traveler" : `${t.travelers} travelers`}</span>
                  <div className="relative flex-1">
                    <span className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-slate-400">$</span>
                    <input type="number" min="0" step="0.01" className={cn(field, "pl-7")} value={t.price} onChange={(e) => setTier(idx, e.target.value)} placeholder="0.00" data-testid={`package-tier-price-${t.travelers}`} />
                  </div>
                  {f.pricing.length > 1 && <button type="button" onClick={() => removeTier(idx)} className="grid h-9 w-9 shrink-0 place-items-center rounded-lg text-slate-400 transition-colors hover:bg-red-50 hover:text-red-600" data-testid={`package-tier-remove-${idx}`}><Trash2 className="h-4 w-4" /></button>}
                </div>
              ))}
            </div>
            <button type="button" onClick={addTier} className="mt-2 inline-flex items-center gap-1.5 text-sm font-medium text-navy-700 transition-colors hover:text-navy-900" data-testid="package-add-tier-btn"><Plus className="h-4 w-4" />Add Traveler Tier</button>
            <p className="mt-1.5 text-xs text-slate-400">Pricing is per person and changes with group size. Travelers pick a package and the price adjusts to how many are travelling.</p>
          </div>
          <label className="block"><span className={label}>Description</span>
            <textarea className={cn(field, "h-24 resize-none py-2.5 leading-relaxed")} value={f.description} onChange={set("description")} placeholder="What's included with this cabin or room type?" data-testid="package-description" /></label>
        </div>
        <div className="mt-2 flex justify-end gap-2">
          <button onClick={onClose} className={btn.secondary} data-testid="package-cancel-btn">Cancel</button>
          <button onClick={save} className={btn.primary} data-testid="package-save-btn">{initial._edit ? "Save package" : "Add package"}</button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

function PackageRow({ p, i, onEdit, onRemove, canEdit }) {
  return (
    <Card className="flex items-start gap-4 p-4" data-testid={`group-package-${i}`}>
      <div className="grid h-16 w-20 shrink-0 place-items-center overflow-hidden rounded-lg bg-slate-100">
        {p.cover ? <img src={p.cover} alt="" className="h-full w-full object-cover" /> : <ImageIcon className="h-5 w-5 text-slate-300" />}
      </div>
      <div className="min-w-0 flex-1">
        <span className="font-semibold text-navy-900" data-testid={`group-package-name-${i}`}>{p.name}</span>
        {p.description && <p className="mt-0.5 line-clamp-2 text-sm text-slate-500">{p.description}</p>}
        <p className="mt-1 text-sm text-slate-600"><span className="font-semibold text-navy-900">from {fmtMoney(pkgFromPrice(p), true)}</span> / person · {p.capacity || 0} spots</p>
      </div>
      {canEdit && (
        <div className="flex shrink-0 gap-1">
          <button onClick={onEdit} className="grid h-9 w-9 place-items-center rounded-lg text-slate-400 transition-colors hover:bg-slate-100 hover:text-navy-900" data-testid={`group-package-edit-${i}`}><Pencil className="h-4 w-4" /></button>
          <button onClick={onRemove} className="grid h-9 w-9 place-items-center rounded-lg text-slate-400 transition-colors hover:bg-red-50 hover:text-red-600" data-testid={`group-package-remove-${i}`}><Trash2 className="h-4 w-4" /></button>
        </div>
      )}
    </Card>
  );
}

function Packages({ group, canEdit }) {
  const { updateGroup } = useDemo();
  const [editing, setEditing] = useState(null);
  const packages = group.packages || [];
  const persist = (next) => updateGroup(group.id, { packages: next });
  const onSave = (clean) => {
    setEditing(null);
    const exists = packages.some((x) => x.id === clean.id);
    persist(exists ? packages.map((x) => (x.id === clean.id ? clean : x)) : [...packages, clean]);
  };
  const remove = (id) => persist(packages.filter((x) => x.id !== id));
  return (
    <div className="mx-auto max-w-2xl space-y-4" data-testid="group-packages">
      <div className="flex items-center justify-between gap-3">
        <div>
          <h2 className="font-display text-lg font-bold text-navy-900">Packages</h2>
          <p className="mt-0.5 text-sm text-slate-500">Cabins, staterooms or room types travelers can choose.</p>
        </div>
        {canEdit && <button onClick={() => setEditing(emptyPackage())} className={btn.secondary} data-testid="group-add-package-btn"><Plus className="h-4 w-4" />Add Package</button>}
      </div>
      {packages.length
        ? <div className="space-y-3">{packages.map((p, i) => <PackageRow key={p.id} p={p} i={i} canEdit={canEdit} onEdit={() => setEditing({ ...p, _edit: true })} onRemove={() => remove(p.id)} />)}</div>
        : <Empty icon={Package} title="No packages yet" text="Add staterooms, cabins or room types with pricing and capacity." />}
      {editing && <PackageModal open onClose={() => setEditing(null)} initial={editing} onSave={onSave} groupId={group.id} />}
    </div>
  );
}

const emptyAddon = () => ({ id: `ao_${Date.now()}`, type: "Travel Insurance", otherLabel: "", description: "", price: "", pricing: "per_person", timing: "immediate", visible: true });

function AddonModal({ open, onClose, initial, onSave }) {
  const [f, setF] = useState(initial);
  const set = (k) => (e) => setF((p) => ({ ...p, [k]: e.target.value }));
  const save = () => {
    const { _edit, ...rest } = f;
    if (rest.type === "Other" && !rest.otherLabel.trim()) return toast.error("Name the add-on in the Other field");
    if (rest.price === "" || Number(rest.price) < 0) return toast.error("Enter a valid price");
    onSave({ ...rest, otherLabel: rest.otherLabel.trim(), description: rest.description.trim(), price: Number(rest.price) || 0 });
  };
  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-lg" data-testid="addon-modal">
        <DialogHeader><DialogTitle>{initial._edit ? "Edit add-on" : "New add-on"}</DialogTitle></DialogHeader>
        <div className="space-y-4">
          <div>
            <span className={label}>Add-on type</span>
            <div className="grid grid-cols-3 gap-2">
              {ADDON_TYPES.map(([t, Icon]) => (
                <button key={t} type="button" onClick={() => setF((p) => ({ ...p, type: t }))} data-testid={`addon-type-${t}`}
                  className={cn("flex flex-col items-center gap-1.5 rounded-xl border px-2 py-3 text-center text-xs font-medium transition-colors", f.type === t ? "border-navy-900 bg-navy-900 text-white" : "border-slate-200 bg-white text-slate-600 hover:border-navy-300")}>
                  <Icon className="h-4 w-4" />{t}
                </button>
              ))}
            </div>
          </div>
          {f.type === "Other" && (
            <label className="block"><span className={label}>Add-on name</span><input className={field} value={f.otherLabel} onChange={set("otherLabel")} placeholder="e.g. Spa Credit" data-testid="addon-other-label" /></label>
          )}
          <label className="block"><span className={label}>Description</span>
            <textarea className={cn(field, "h-24 resize-none py-2.5 leading-relaxed")} value={f.description} onChange={set("description")} placeholder="What's included with this add-on?" data-testid="addon-description" /></label>
          <div className="grid grid-cols-2 gap-3">
            <label className="block"><span className={label}>Price (USD)</span>
              <input type="number" min="0" step="0.01" className={field} value={f.price} onChange={set("price")} placeholder="0.00" data-testid="addon-price" /></label>
            <label className="block"><span className={label}>Pricing</span>
              <select className={field} value={f.pricing} onChange={set("pricing")} data-testid="addon-pricing">
                <option value="per_person">Per person</option><option value="per_booking">Per booking</option></select></label>
          </div>
          <div>
            <span className={label}>Payment timing</span>
            <div className="flex gap-2">
              {[["immediate", "Charge immediately", Zap], ["later", "Charge later", Clock]].map(([v, l, Icon]) => (
                <button key={v} type="button" onClick={() => setF((p) => ({ ...p, timing: v }))} data-testid={`addon-timing-${v}`}
                  className={cn("flex flex-1 items-center justify-center gap-1.5 rounded-xl border px-3 py-2.5 text-sm font-medium transition-colors", f.timing === v ? "border-navy-900 bg-navy-900 text-white" : "border-slate-200 bg-white text-slate-600 hover:border-navy-300")}>
                  <Icon className="h-4 w-4" />{l}
                </button>
              ))}
            </div>
          </div>
          <div className="flex items-center justify-between rounded-xl border border-slate-200 px-3.5 py-3">
            <div><p className="text-sm font-medium text-navy-900">Visible to clients?</p><p className="text-xs text-slate-500">Show this add-on on the group booking page.</p></div>
            <Switch checked={f.visible} onCheckedChange={(v) => setF((p) => ({ ...p, visible: v }))} data-testid="addon-visible" />
          </div>
        </div>
        <div className="mt-2 flex justify-end gap-2">
          <button onClick={onClose} className={btn.secondary} data-testid="addon-cancel-btn">Cancel</button>
          <button onClick={save} className={btn.primary} data-testid="addon-save-btn">{initial._edit ? "Save add-on" : "Add add-on"}</button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

function AddonRow({ a, onEdit, onRemove, i, canEdit }) {
  const Icon = addonIcon(a.type);
  const name = a.type === "Other" ? a.otherLabel : a.type;
  return (
    <Card className="flex items-start gap-3.5 p-4" data-testid={`group-addon-${i}`}>
      <span className="grid h-10 w-10 shrink-0 place-items-center rounded-xl bg-navy-50 text-navy-700"><Icon className="h-5 w-5" /></span>
      <div className="min-w-0 flex-1">
        <div className="flex flex-wrap items-center gap-2">
          <span className="font-semibold text-navy-900" data-testid={`group-addon-name-${i}`}>{name}</span>
          {a.type === "Other" && <span className="rounded-md bg-slate-100 px-1.5 py-0.5 text-[11px] font-medium text-slate-500">Other</span>}
          {a.visible
            ? <span className="inline-flex items-center gap-1 rounded-md bg-emerald-50 px-1.5 py-0.5 text-[11px] font-medium text-emerald-700"><Eye className="h-3 w-3" />Visible</span>
            : <span className="inline-flex items-center gap-1 rounded-md bg-slate-100 px-1.5 py-0.5 text-[11px] font-medium text-slate-500"><EyeOff className="h-3 w-3" />Hidden</span>}
        </div>
        {a.description && <p className="mt-1 text-sm text-slate-600">{a.description}</p>}
        <div className="mt-2 flex flex-wrap items-center gap-x-3 gap-y-1 text-xs text-slate-500">
          <span className="font-semibold text-navy-900">{fmtMoney(a.price, !Number.isInteger(a.price))}</span>
          <span>{a.pricing === "per_person" ? "per person" : "per booking"}</span>
          <span className="inline-flex items-center gap-1">{a.timing === "immediate" ? <><Zap className="h-3 w-3" />Charge immediately</> : <><Clock className="h-3 w-3" />Charge later</>}</span>
        </div>
      </div>
      {canEdit && (
        <div className="flex shrink-0 gap-1">
          <button onClick={onEdit} className="grid h-9 w-9 place-items-center rounded-lg text-slate-400 transition-colors hover:bg-slate-100 hover:text-navy-900" data-testid={`group-addon-edit-${i}`}><Pencil className="h-4 w-4" /></button>
          <button onClick={onRemove} className="grid h-9 w-9 place-items-center rounded-lg text-slate-400 transition-colors hover:bg-red-50 hover:text-red-600" data-testid={`group-addon-remove-${i}`}><Trash2 className="h-4 w-4" /></button>
        </div>
      )}
    </Card>
  );
}

function AddOns({ group, canEdit }) {
  const { updateGroup } = useDemo();
  const addons = group.addons || [];
  const [editing, setEditing] = useState(null);

  const persist = (next) => updateGroup(group.id, { addons: next });
  const onSave = (a) => {
    const clean = { ...a }; delete clean._edit;
    const exists = addons.some((x) => x.id === clean.id);
    persist(exists ? addons.map((x) => (x.id === clean.id ? clean : x)) : [...addons, clean]);
    setEditing(null);
    toast.success(exists ? "Add-on updated" : "Add-on added");
  };
  const remove = (id) => persist(addons.filter((x) => x.id !== id));

  return (
    <div className="mx-auto max-w-2xl space-y-4" data-testid="group-addons">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="font-display text-lg font-bold text-navy-900">Add-Ons</h2>
          <p className="text-sm text-slate-500">Optional extras travelers can book with this group.</p>
        </div>
        {canEdit && <button onClick={() => setEditing(emptyAddon())} className={btn.secondary} data-testid="group-add-addon-btn"><Plus className="h-4 w-4" />Add add-on</button>}
      </div>
      {addons.length
        ? <div className="space-y-3">{addons.map((a, i) => <AddonRow key={a.id} a={a} i={i} canEdit={canEdit} onEdit={() => setEditing({ ...a, _edit: true })} onRemove={() => remove(a.id)} />)}</div>
        : <Empty icon={Sparkles} title="No add-ons yet" text="Add optional extras like insurance, drink packages or excursions travelers can book." />}
      {editing && <AddonModal open onClose={() => setEditing(null)} initial={editing} onSave={onSave} />}
    </div>
  );
}

export default function GroupWorkspace() {
  const { id } = useParams();
  const { group: get, me, isAdmin } = useDemo();
  const [params, setParams] = useSearchParams();
  const group = get(id);
  if (!group) return <Navigate to="/agent/groups" replace />;
  const tab = params.get("tab") || "overview";
  const canEdit = isAdmin || group.agentId === me?.id;

  return (
    <div data-testid="group-workspace">
      <Hero group={group} />
      <div className="no-scrollbar sticky top-0 z-10 -mx-4 mb-6 mt-4 flex gap-1 overflow-x-auto border-b border-slate-200 bg-[#F5F7FA] px-4 sm:mx-0 sm:px-0">
        {TABS.map(([v, l]) => (
          <button key={v} data-testid={`group-tab-${v}`} onClick={() => setParams({ tab: v }, { replace: true })}
            className={cn("-mb-px whitespace-nowrap border-b-2 px-4 py-3 text-sm font-medium transition-colors", tab === v ? "border-navy-900 text-navy-900" : "border-transparent text-slate-500 hover:text-navy-900")}>{l}</button>
        ))}
      </div>
      {tab === "overview" && <Overview group={group} canEdit={canEdit} />}
      {tab === "packages" && <Packages group={group} canEdit={canEdit} />}
      {tab === "addons" && <AddOns group={group} canEdit={canEdit} />}
      {tab === "itinerary" && <Itinerary group={group} canEdit={canEdit} />}
      {tab === "bookings" && <Bookings group={group} />}
    </div>
  );
}
